import { RefObject, useEffect, useRef, useState } from 'react';

export interface UseResizeObserverProps {
  timelineRef: RefObject<HTMLDivElement>;
  eventRefs: RefObject<(HTMLDivElement | null)[]>;
  currentVersionDotRef: RefObject<HTMLDivElement | null>;
  lastEventDotRef: RefObject<HTMLDivElement | null>;
}

export const useResizeObserver = ({
  timelineRef,
  eventRefs,
  currentVersionDotRef,
  lastEventDotRef,
}: UseResizeObserverProps) => {
  // Force re-render when positions need to be updated
  const [positionsUpdateKey, setPositionsUpdateKey] = useState(0);

  // ResizeObserver ref
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Update handler positions when sizes change
  useEffect(() => {
    // Function to update positions
    const updatePositions = () => {
      setPositionsUpdateKey((prev) => prev + 1);
    };

    // Create a resize observer to watch for size changes in events
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(updatePositions);

      // Observe the timeline container
      if (timelineRef.current) {
        resizeObserverRef.current.observe(timelineRef.current);
      }

      // Observe each event element
      if (eventRefs.current) {
        eventRefs.current.forEach((ref) => {
          if (ref) {
            resizeObserverRef.current?.observe(ref);
          }
        });
      }

      // Observe dot references
      if (currentVersionDotRef.current) {
        resizeObserverRef.current.observe(currentVersionDotRef.current);
      }

      if (lastEventDotRef.current) {
        resizeObserverRef.current.observe(lastEventDotRef.current);
      }
    }

    // Add window resize listener as a fallback
    window.addEventListener('resize', updatePositions);

    // Clean up
    return () => {
      window.removeEventListener('resize', updatePositions);

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [timelineRef, eventRefs, currentVersionDotRef, lastEventDotRef]);

  // Update observer when event refs change
  useEffect(() => {
    // Add any new elements to the observer
    if (resizeObserverRef.current && eventRefs.current) {
      eventRefs.current.forEach((ref) => {
        if (ref) {
          resizeObserverRef.current?.observe(ref);
        }
      });

      if (currentVersionDotRef.current) {
        resizeObserverRef.current.observe(currentVersionDotRef.current);
      }

      if (lastEventDotRef.current) {
        resizeObserverRef.current.observe(lastEventDotRef.current);
      }
    }
  }, [eventRefs.current?.length, currentVersionDotRef, lastEventDotRef]);

  return { positionsUpdateKey };
};

export default useResizeObserver;
